/* ---------------------- Menu ---------------------- */

import { FILE_PREFIX, TlaFile } from '@tldraw/dotcom-shared'
import { Fragment, ReactNode, useCallback, useId } from 'react'
import { useNavigate } from 'react-router-dom'
import {
	defineMessages,
	getIncrementedName,
	TldrawUiDropdownMenuContent,
	TldrawUiDropdownMenuRoot,
	TldrawUiDropdownMenuTrigger,
	TldrawUiMenuCheckboxItem,
	TldrawUiMenuContextProvider,
	TldrawUiMenuGroup,
	TldrawUiMenuItem,
	TldrawUiMenuSubmenu,
	uniqueId,
	useDialogs,
	useMaybeEditor,
	useToasts,
	useTranslation,
	useValue,
} from 'tldraw'
import { routes } from '../../../routeDefs'
import { TldrawApp } from '../../app/TldrawApp'
import { useActiveWorkspaceId } from '../../hooks/useActiveWorkspaceId'
import { useApp } from '../../hooks/useAppState'
import { useHasFileAdminRights } from '../../hooks/useIsFileOwner'
import { useIsFilePinned } from '../../hooks/useIsFilePinned'
import { TLAppUiEventSource, useTldrawAppUiEvents } from '../../utils/app-ui-events'
import { copyTextToClipboard } from '../../utils/copy'
import { CreateWorkspaceDialog } from '../dialogs/CreateWorkspaceDialog'
import { TlaDeleteFileDialog } from '../dialogs/TlaDeleteFileDialog'
import { TLA_MENU_POSITION } from '../tla-menu/tla-menu'
import { editorMessages } from '../TlaEditor/editor-messages'
import { downloadAppFile } from '../TlaEditor/useFileEditorOverrides'

const messages = defineMessages({
	copied: { defaultMessage: 'Copied link' },
	copyLink: { defaultMessage: 'Copy link' },
	delete: { defaultMessage: 'Delete' },
	duplicate: { defaultMessage: 'Duplicate' },
	forget: { defaultMessage: 'Forget' },
	rename: { defaultMessage: 'Rename' },
	copy: { defaultMessage: 'Copy' },
	pin: { defaultMessage: 'Pin file' },
	unpin: { defaultMessage: 'Unpin file' },
	myWorkspace: { defaultMessage: 'My workspace' },
})

function getDuplicateName(file: TlaFile, app: TldrawApp) {
	if (file.name.trim().length === 0) {
		return ''
	}
	const currentFileName = app.getFileName(file.id)
	const allFileNames = app.getUserOwnFiles().map((file) => file.name)
	return getIncrementedName(currentFileName, allFileNames)
}

export function TlaFileMenu({
	children,
	source,
	fileId,
	workspaceId,
	onRenameAction,
	trigger,
}: {
	children?: ReactNode
	source: TLAppUiEventSource
	fileId: string
	workspaceId: string | null
	onRenameAction(): void
	trigger: ReactNode
}) {
	const id = useId()
	const fileItemsWhenNoChildren = (
		<FileItems
			source={source}
			fileId={fileId}
			onRenameAction={onRenameAction}
			workspaceId={workspaceId}
		/>
	)
	return (
		<TldrawUiDropdownMenuRoot id={`file-menu-${fileId}-${source}-${id}`}>
			<TldrawUiMenuContextProvider type="menu" sourceId="dialog">
				<TldrawUiDropdownMenuTrigger>{trigger}</TldrawUiDropdownMenuTrigger>
				{/* Sidebar menus hang ~8px over the sidebar's right edge, so they keep the shared
				    side/collision offsets but override alignOffset (the default is tuned for the
				    share/select menus, which would leave these looking inset). */}
				<TldrawUiDropdownMenuContent
					side="bottom"
					align="end"
					{...TLA_MENU_POSITION}
					alignOffset={-16}
				>
					{children ?? fileItemsWhenNoChildren}
				</TldrawUiDropdownMenuContent>
			</TldrawUiMenuContextProvider>
		</TldrawUiDropdownMenuRoot>
	)
}

export function FileItems({
	source,
	fileId,
	onRenameAction,
	workspaceId,
}: {
	source: TLAppUiEventSource
	fileId: string
	onRenameAction(): void
	workspaceId: string | null
}) {
	const msg = useTranslation()
	const app = useApp()
	const editor = useMaybeEditor()
	const { addDialog } = useDialogs()
	const navigate = useNavigate()
	const { addToast } = useToasts()
	const trackEvent = useTldrawAppUiEvents()
	const copiedMsg = msg(messages.copied)
	const hasAdminRights = useHasFileAdminRights(fileId)
	const isPinned = useIsFilePinned(fileId, workspaceId ?? '')
	const activeWorkspaceId = useActiveWorkspaceId()

	const file = useValue('file', () => app.getFile(fileId), [app, fileId])
	const homeWorkspaceId = app.getHomeWorkspaceId()

	// Get all workspace memberships (including the home workspace, filtered out below)
	const workspaceMemberships = useValue(
		'workspaceMemberships',
		() => app.getWorkspaceMemberships(),
		[app]
	)

	// A file lives in exactly one workspace. The "Move to" menu is a checklist of every
	// destination — the home workspace plus each non-home workspace — with the file's own
	// workspace checked. The home workspace is rendered separately (it's always the first item),
	// labelled with its own name like any other workspace.
	// (This is the workspace the file belongs to, which is not necessarily one the current user
	// can write to — for that, see activeWorkspaceId above.)
	const fileWorkspaceId = file?.owningGroupId ?? homeWorkspaceId
	const homeWorkspaceName = workspaceMemberships.find((g) => g.groupId === homeWorkspaceId)?.group
		?.name
	const moveToWorkspaces = workspaceMemberships.filter(
		(g): g is typeof g & { group: NonNullable<(typeof g)['group']> } =>
			g.groupId !== homeWorkspaceId && !!g.group
	)

	const handleCopyLinkClick = useCallback(() => {
		const url = routes.tlaFile(fileId, { asUrl: true })
		// The mounted editor is the open file's, which a sidebar row may not be.
		const deepLink = source === 'file-header' ? editor?.createDeepLink({ url }).toString() : null
		copyTextToClipboard(deepLink ?? url)
		addToast({
			id: 'copied-link',
			title: copiedMsg,
		})
		trackEvent('copy-file-link', { source })
	}, [fileId, addToast, copiedMsg, trackEvent, source, editor])

	const handlePinUnpinClick = useCallback(async () => {
		if (!workspaceId) return
		if (app.isPinned(fileId, workspaceId)) {
			app.z.mutate.unpinFile({ fileId, workspaceId })
		} else {
			app.z.mutate.pinFile({ fileId, workspaceId })
		}
	}, [app, fileId, workspaceId])

	const handleDuplicateClick = useCallback(async () => {
		// The sidebar passes the workspace the file is listed under; the file header doesn't, so
		// fall back to the user's active workspace. This matches the sidebar (and unlike the file's
		// own owning workspace, it's always one the user can write to — e.g. a guest duplicating a
		// shared file gets the copy in their home workspace, not the owner's).
		const targetWorkspaceId = workspaceId ?? activeWorkspaceId
		if (!targetWorkspaceId) return
		const newFileId = uniqueId()
		const file = app.getFile(fileId)
		if (!file) return
		trackEvent('duplicate-file', { source })
		const res = await app.createFile({
			fileId: newFileId,
			workspaceId: targetWorkspaceId,
			name: getDuplicateName(file, app),
			createSource: `${FILE_PREFIX}/${fileId}`,
		})
		// copy the state too
		const prevState = app.getFileState(fileId)
		app.updateFileState(newFileId, {
			lastSessionState: prevState?.lastSessionState,
		})
		if (res.ok) {
			app.sidebarState.update((prev) => ({
				...prev,
				renameState: { fileId: newFileId, workspaceId: targetWorkspaceId },
			}))
			navigate(routes.tlaFile(newFileId))
		}
	}, [app, fileId, workspaceId, activeWorkspaceId, navigate, trackEvent, source])

	const handleDeleteClick = useCallback(() => {
		if (!workspaceId) return
		addDialog({
			component: ({ onClose }) => (
				<TlaDeleteFileDialog workspaceId={workspaceId} fileId={fileId} onClose={onClose} />
			),
		})
	}, [fileId, addDialog, workspaceId])

	const handleDownloadClick = useCallback(() => {
		trackEvent('download-file', { source })
		downloadAppFile(fileId)
	}, [fileId, source, trackEvent])

	const unpinMsg = msg(messages.unpin)

	return (
		<Fragment>
			<TldrawUiMenuGroup id="file-actions">
				{/* todo: in published rooms, support copying link */}
				<TldrawUiMenuItem
					label={msg(messages.copyLink)}
					id="copy-link"
					readonlyOk
					onSelect={handleCopyLinkClick}
				/>
				{hasAdminRights && (
					<TldrawUiMenuItem
						label={msg(messages.rename)}
						id="rename"
						readonlyOk
						onSelect={onRenameAction}
					/>
				)}
				{/* todo: in published rooms, support duplication / forking */}
				<TldrawUiMenuItem
					label={msg(messages.duplicate)}
					id="duplicate"
					readonlyOk
					onSelect={handleDuplicateClick}
				/>
				<TldrawUiMenuItem
					label={msg(editorMessages.downloadFile)}
					id="download-file"
					readonlyOk
					onSelect={handleDownloadClick}
				/>
				{workspaceId && (
					<TldrawUiMenuItem
						label={isPinned ? unpinMsg : msg(messages.pin)}
						id="pin-unpin"
						readonlyOk
						onSelect={handlePinUnpinClick}
					/>
				)}
			</TldrawUiMenuGroup>
			<TldrawUiMenuGroup id="file-delete">
				{hasAdminRights && (
					<TldrawUiMenuSubmenu id="move-to-workspace" label={'Move to'} size="small">
						<TldrawUiMenuGroup id="workspaces">
							<TldrawUiMenuCheckboxItem
								key="my-files"
								label={homeWorkspaceName ?? msg(messages.myWorkspace)}
								id="my-files"
								readonlyOk
								checked={fileWorkspaceId === homeWorkspaceId}
								onSelect={() => {
									if (fileWorkspaceId === homeWorkspaceId) return
									app.z.mutate.moveFileToWorkspace({ fileId, workspaceId: homeWorkspaceId })
								}}
							/>
							{moveToWorkspaces.map((membership) => (
								<TldrawUiMenuCheckboxItem
									key={membership.groupId}
									label={membership.group.name}
									id={`workspace-${membership.groupId}`}
									readonlyOk
									checked={membership.groupId === fileWorkspaceId}
									onSelect={() => {
										if (membership.groupId === fileWorkspaceId) return
										app.z.mutate.moveFileToWorkspace({ fileId, workspaceId: membership.groupId })
									}}
								/>
							))}
						</TldrawUiMenuGroup>
						<TldrawUiMenuGroup id="create-new-workspace">
							<TldrawUiMenuItem
								label="New workspace"
								id="create-new-workspace"
								iconLeft={'plus'}
								readonlyOk
								onSelect={() => {
									addDialog({
										component: ({ onClose }) => (
											<CreateWorkspaceDialog
												onClose={onClose}
												onCreate={async (name) => {
													const id = uniqueId()
													const createRes = await app.z.mutate.createWorkspace({ id, name }).client
													if (createRes.type === 'error') {
														app.showMutationRejectionToast(createRes.error)
														return
													}
													trackEvent('create-workspace', { source })
													const moveRes = await app.z.mutate.moveFileToWorkspace({
														fileId,
														workspaceId: id,
													}).client
													if (moveRes.type === 'error') {
														// the workspace was created; only the move failed
														app.showMutationRejectionToast(moveRes.error)
													}
												}}
											/>
										),
									})
								}}
							/>
						</TldrawUiMenuGroup>
					</TldrawUiMenuSubmenu>
				)}
				{workspaceId && (
					<TldrawUiMenuItem
						label={msg(hasAdminRights ? messages.delete : messages.forget)}
						id="delete"
						readonlyOk
						onSelect={handleDeleteClick}
					/>
				)}
			</TldrawUiMenuGroup>
		</Fragment>
	)
}
